<script setup>
import { ref, watch, computed, onBeforeUnmount } from "vue";
import { ChatDotRound } from "@element-plus/icons-vue";
import SocialFundRichText from "./SocialFundRichText.vue";
import SocialFundMessageEditor from "./SocialFundMessageEditor.vue";
import { socialFundRichTextContent } from "@/utils/socialFundRichText";
import { useChatScroll } from "@/app/composables/useChatScroll";
import SharedExpandableContent from "@/components/shared/SharedExpandableContent.vue";
import Service from "@/api/services/socialFundApplication.service";
import Notification from "@/utils/plugins/NotificationService";
import { useUiStore } from "@/app/stores/uiStore";
import { apiErrorMessage, collectionItems } from "@/utils/socialFundV2";
const props = defineProps({
  applicationId: { type: [String, Number], required: true },
  status: { type: String, default: "" },
  // Backend (chatOpen): el chat admite mensajes hasta que se cierra la
  // convocatoria, aunque el expediente esté cerrado.
  chatOpen: { type: Boolean, default: true },
  canSend: Boolean,
});
const uiStore = useUiStore();
const messages = ref([]);
const chatThread = ref(null);
useChatScroll(chatThread, () => {
  const last = messages.value[messages.value.length - 1];
  return [props.applicationId, messages.value.length, last?.id, last?.content].join("\u0000");
});
const content = ref("");
const editorKey = ref(0);
const hasContent = computed(() => !!socialFundRichTextContent(content.value));
const error = ref("");
const sending = ref(false);
let generation = 0;
let disposed = false;
const writable = computed(() => props.canSend && props.chatOpen);
const notice = computed(() =>
  !props.chatOpen
    ? "La convocatoria de este expediente está cerrada: el chat es de solo lectura."
    : ["received", "in_review_a51", "pending_director_review"].includes(props.status)
      ? "El solicitante recibirá este mensaje por email, pero todavía no tiene acceso a su expediente para responder."
      : "",
);
const load = async () => {
  const current = ++generation;
  uiStore.startLoading();
  error.value = "";
  try {
    const response = await Service.getMessages(props.applicationId);
    if (disposed || current !== generation) return;
    messages.value = collectionItems(response.data)
      .slice()
      .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
  } catch (err) {
    if (!disposed && current === generation)
      error.value = await apiErrorMessage(
        err,
        "No se ha podido cargar la conversación.",
      );
  } finally {
    uiStore.stopLoading();
  }
};
watch(
  () => props.applicationId,
  () => {
    generation++;
    messages.value = [];
    content.value = "";
    editorKey.value += 1;
    error.value = "";
    load();
  },
  { immediate: true },
);
onBeforeUnmount(() => {
  disposed = true;
  generation++;
});
const send = async () => {
  if (!writable.value || sending.value || !hasContent.value) return;
  const id = props.applicationId;
  const draft = content.value.trim();
  sending.value = true;
  uiStore.startLoading();
  try {
    await Service.addMessage(id, { content: draft });
    if (disposed || id !== props.applicationId) return;
    content.value = "";
    editorKey.value += 1;
    Notification.success("Mensaje enviado al solicitante.", 3500);
    await load();
  } catch (err) {
    Notification.addNotification(
      await apiErrorMessage(
        err,
        "No se ha podido enviar el mensaje. El borrador se conserva.",
      ),
      "error",
      6000,
    );
  } finally {
    sending.value = false;
    uiStore.stopLoading();
  }
};
const formatDate = (value) =>
  value ? new Date(value).toLocaleString("es-ES") : "";
</script>

<template>
  <el-card class="applicant-chat">
    <div class="applicant-chat__heading">
      <span class="applicant-chat__heading-icon" aria-hidden="true"><el-icon><ChatDotRound /></el-icon></span>
      <h3>Chat con el solicitante</h3>
    </div>
    <p class="applicant-chat__audience">
      Los mensajes de esta sección se envían al solicitante. El chat interno
      está en una sección independiente.
    </p>
    <p v-if="error" role="alert">
      {{ error }}
      <button type="button" class="btn btn_yellow" @click="load">
        REINTENTAR
      </button>
    </p>
    <p v-else-if="!messages.length">Todavía no hay mensajes.</p>
    <ol
      ref="chatThread"
      class="applicant-chat__messages"
      aria-label="Conversación con el solicitante"
    >
      <li
        v-for="message in messages"
        :key="message.id"
        class="applicant-chat__message social-fund-chat-message"
        :class="{
          'applicant-chat__message--staff': message.authorType !== 'applicant',
        }"
      >
        <strong
          >{{ message.authorName }} · {{ message.authorTypeLabel }}</strong
        >
        <small v-if="message.broadcastId">Comunicado</small>
        <SharedExpandableContent :content-key="message.content">
          <SocialFundRichText :content="message.content" />
        </SharedExpandableContent>
        <time>{{ formatDate(message.createdAt) }}</time>
      </li>
    </ol>
    <form v-if="writable" @submit.prevent="send">
      <p v-if="notice" class="applicant-chat__notice">{{ notice }}</p>
      <label id="applicant-chat-content-label">Mensaje para el solicitante</label>
      <SocialFundMessageEditor :key="editorKey" v-model="content"
        label-id="applicant-chat-content-label" :disabled="sending" />
      <button
        type="submit"
        class="btn btn_red"
        :disabled="sending || !hasContent"
      >
        {{ sending ? "ENVIANDO..." : "ENVIAR AL SOLICITANTE" }}
      </button>
    </form>
    <p v-else-if="notice" class="applicant-chat__notice">{{ notice }}</p>
  </el-card>
</template>

<style scoped lang="scss">
.applicant-chat {
  margin-bottom: 20px;
  border-radius: 15px;
  border-top: 5px solid #409eff;
}
.applicant-chat :deep(.el-card__body) { padding-top: 17px; }
.applicant-chat__heading {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 15px;
}
.applicant-chat__heading-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 42px;
  height: 42px;
  border-radius: 13px;
  background: #edeeff;
  color: #474ce6;
  font-size: 22px;
}
.applicant-chat__heading h3 {
  margin: 0;
  min-width: 0;
  color: #202845;
  font-size: 18px;
  font-weight: 900;
  overflow-wrap: anywhere;
}
.applicant-chat__audience,
.applicant-chat__notice {
  background: #eef3ff;
  padding: 12px;
  border-radius: 10px;
}
.applicant-chat__messages {
  list-style: none;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-height: 500px;
  overflow-y: auto;
}
.applicant-chat__message {
  align-self: flex-start;
  max-width: 90%;
  background: #f4f4f6;
  padding: 12px;
  border-radius: 12px;
  overflow-wrap: anywhere;
}
.applicant-chat__message--staff {
  align-self: flex-end;
  background: #eff0ff;
}
.applicant-chat__message p {
  white-space: pre-wrap;
  margin: 8px 0;
}
.applicant-chat__message time,
.applicant-chat__message small {
  display: block;
}
.applicant-chat form {
  padding-bottom: 16px;
}
.applicant-chat form .btn {
  align-items: center;
  display: inline-flex;
  justify-content: center;
  font-size: 11px;
  font-weight: 800;
  height: auto;
  min-height: 44px;
  min-width: 158px;
  width: 158px;
  max-width: 100%;
  padding: 6px 14px;
  margin: 20px 0 0;
  color: #fff;
}
@media (max-width: 767px) {
  .applicant-chat :deep(.el-card__body) { padding-top: 15px; }
  .applicant-chat__heading { gap: 10px; }
  .applicant-chat__heading h3 { font-size: 14px; letter-spacing: -.02em; }
  .applicant-chat form .btn {
    min-width: 0;
    width: 100%;
  }
  .applicant-chat__message {
    max-width: 100%;
  }
}
</style>
